import { Box, Typography, Grid, Paper, alpha } from "@mui/material";
import { Groups2Outlined } from "@mui/icons-material";
import ParticipantCard from "./ParticipantCard";
import { IDelegate } from "../../../interfaces/IDelegate";
import { IDelegateRole } from "../../../interfaces/IDelegateRole";
import { ISessionDelegate } from "../../../interfaces/ISessionDelegate";

function getRoleName(roleId: string, delegateRoles: IDelegateRole[]): string {
    const found = delegateRoles.find(r => r.id === roleId);
    return found ? found.name : "Unknown Role";
}

function groupSessionDelegatesByRole(
    sessionDelegates: ISessionDelegate[],
    delegateRoles: IDelegateRole[]
): Record<string, ISessionDelegate[]> {
    return sessionDelegates.reduce((acc, sd) => {
        const roleName = getRoleName(sd.roleId, delegateRoles);
        if (!acc[roleName]) acc[roleName] = [];
        const alreadyAdded = acc[roleName].some(existing => existing.delegateId === sd.delegateId);
        if (!alreadyAdded) acc[roleName].push(sd);
        return acc;
    }, {} as Record<string, ISessionDelegate[]>);
}

export default function EventDelegates({
    sessionDelegates,
    delegates,
    delegateRoles,
    onCardClick,
    primaryColor = '#493193',
    secondaryColor = '#2fadd8',
}: {
    sessionDelegates: ISessionDelegate[];
    delegates: IDelegate[];
    delegateRoles: IDelegateRole[];
    onCardClick?: (d: IDelegate, roleId?: string) => void;
    primaryColor?: string;
    secondaryColor?: string;
}) {
    const grouped = groupSessionDelegatesByRole(sessionDelegates, delegateRoles);
    const groupedEntries = Object.entries(grouped)
        .map(([role, roleSessionDelegates]) => ({
            role,
            roleId: roleSessionDelegates[0]?.roleId,
            delegates: roleSessionDelegates
                .map((sessionDelegate) => ({
                    sessionDelegate,
                    delegate: delegates.find(d => d.id === sessionDelegate.delegateId),
                }))
                .filter((item): item is { sessionDelegate: ISessionDelegate; delegate: IDelegate } => Boolean(item.delegate)),
        }))
        .filter((entry) => entry.delegates.length > 0);

    return (
        <Box sx={{ mb: 2 }}>
            <Paper
                elevation={2}
                sx={{
                    p: { xs: 2, md: 4 },
                    mb: { xs: 3, md: 4 },
                    borderRadius: 3,
                    border: `1px solid ${alpha(primaryColor, 0.1)}`,
                    boxShadow: `0 4px 20px ${alpha(primaryColor, 0.06)}`,
                }}
            >
                <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                        fontWeight: 700,
                        mb: 1,
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}
                >
                    Delegates
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                    Meet the speakers, organisers, and staff who make this event possible.
                </Typography>
            </Paper>
            <Paper
                elevation={2}
                sx={{
                    p: { xs: 2, md: 4 },
                    mb: { xs: 3, md: 4 },
                    borderRadius: 3,
                    border: `1px solid ${alpha(primaryColor, 0.1)}`,
                    boxShadow: `0 4px 20px ${alpha(primaryColor, 0.06)}`,
                }}
            >
                {groupedEntries.length === 0 ? (
                    <Box
                        sx={{
                            minHeight: 220,
                            px: { xs: 2, md: 4 },
                            py: { xs: 4, md: 6 },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            borderRadius: 2.5,
                            border: `1px dashed ${alpha(primaryColor, 0.18)}`,
                            background: `linear-gradient(180deg, ${alpha(primaryColor, 0.04)} 0%, ${alpha(secondaryColor, 0.05)} 100%)`,
                        }}
                    >
                        <Box
                            sx={{
                                width: 64,
                                height: 64,
                                mb: 2,
                                borderRadius: '50%',
                                display: 'grid',
                                placeItems: 'center',
                                bgcolor: alpha(primaryColor, 0.1),
                                color: primaryColor,
                            }}
                        >
                            <Groups2Outlined sx={{ fontSize: 30 }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                            No delegates confirmed yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, lineHeight: 1.7 }}>
                            Speaker, organiser, and staff profiles will appear here once delegates have been assigned to this event.
                        </Typography>
                    </Box>
                ) : groupedEntries.map(({ role, delegates: roleDelegates }) => (
                    <Box key={role} sx={{ mb: 5 }}>
                        <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                                fontWeight: 700,
                                mb: 2,
                                textAlign: "left",
                                color: primaryColor,
                            }}
                        >
                            {role} <Typography component="span" sx={{ fontWeight: 400, color: alpha(primaryColor, 0.6) }}>({roleDelegates.length})</Typography>
                        </Typography>
                        <Grid container spacing={4} justifyContent="flex-start" alignItems="stretch" wrap="wrap">
                            {roleDelegates.map(({ sessionDelegate, delegate }) => (
                                <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }} key={delegate.id}>
                                    <ParticipantCard
                                        participant={delegate}
                                        role={role}
                                        onClick={() => onCardClick && onCardClick(delegate, sessionDelegate.roleId)}
                                        primaryColor={primaryColor}
                                        secondaryColor={secondaryColor}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                ))}
            </Paper>
        </Box>
    );
}